import { BundleBuilderStoreMixinComponent } from '@theme/bundle-builder-mixin-store';
import { TIER_CONFIG } from '@theme/bundle-builder-tier-config';

/** @typedef {import('./bundle-builder-tier-config.js').TierEntry} TierEntry */

/**
 * Vertical discount tier tracker. Tier steps and copy are rendered in Liquid;
 * this component updates fill height, step active states, and progressbar ARIA.
 */
export class BundleBuilderTierTrackerComponent extends BundleBuilderStoreMixinComponent {
  requiredRefs = [
    'tierTrackerBar',
    'tierTrackerFill',
    'tierTrackerSteps',
  ];

  /** @type {number} */
  #minTierThreshold = Math.min(...TIER_CONFIG.map((tier) => tier.minQuantity));

  /** @type {number} */
  #maxTierThreshold = Math.max(...TIER_CONFIG.map((tier) => tier.minQuantity));

  onStateUpdate() {
    this.#renderTierTracker();
  }

  /**
   * Fill is 0% at the minimum tier; scales from min → max tier only.
   *
   * @param {number} totalQuantity
   * @returns {number}
   */
  #getFillPercent(totalQuantity) {
    const min = this.#minTierThreshold;
    const max = this.#maxTierThreshold;
    const span = max - min;
    if (span <= 0 || totalQuantity <= min) return 0;
    return Math.min(((totalQuantity - min) / span) * 100, 100);
  }

  #renderTierTracker() {
    const totalQuantity = this.getters.getTotalQuantity();
    const minThreshold = this.#minTierThreshold;
    const maxThreshold = this.#maxTierThreshold;
    const tierSpan = maxThreshold - minThreshold;

    const fillPercent = this.#getFillPercent(totalQuantity);
    /** @type {HTMLElement} */ (this.refs.tierTrackerFill).style.height = `${fillPercent}%`;

    const thresholdEls = this.querySelectorAll('[data-tier-threshold]');
    for (const el of thresholdEls) {
      const threshold = Number(el.getAttribute('data-tier-threshold'));
      el.setAttribute('data-active', totalQuantity >= threshold ? 'true' : 'false');
    }

    const bar = /** @type {HTMLElement} */ (this.refs.tierTrackerBar);
    const progressValue = Math.max(0, Math.min(totalQuantity - minThreshold, tierSpan));
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuenow', String(progressValue));
    bar.setAttribute('aria-valuemax', String(tierSpan));
    const valueTextTemplate = bar.dataset.templateValueText ?? '';
    if (valueTextTemplate) {
      bar.setAttribute(
        'aria-valuetext',
        valueTextTemplate
          .replace('[count]', String(totalQuantity))
          .replace('[max]', String(maxThreshold))
      );
    }
  }
}

if (!customElements.get('bundle-builder-tier-tracker-component')) {
  customElements.define('bundle-builder-tier-tracker-component', BundleBuilderTierTrackerComponent);
}
